import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';
import { getErrorMessage, logError, logInfo } from '../utils';

/**
 * Configuration options for the API client
 */
export interface ApiClientConfig {
  /** Base URL for API requests */
  baseURL: string;
  /** Default timeout in milliseconds */
  timeout?: number;
  /** Default headers to include with every request */
  headers?: Record<string, string>;
  /** Maximum number of retry attempts for failed requests */
  maxRetries?: number;
  /** Delay between retry attempts in milliseconds */
  retryDelay?: number;
  /** Whether to automatically handle common errors */
  handleErrors?: boolean;
}

/**
 * Request interceptor function type
 */
export type RequestInterceptor = (
  config: InternalAxiosRequestConfig
) => InternalAxiosRequestConfig | Promise<InternalAxiosRequestConfig>;

/**
 * Response interceptor function type
 */
export type ResponseInterceptor<T = unknown> = (
  response: AxiosResponse<T>
) => AxiosResponse<T> | Promise<AxiosResponse<T>>;

/**
 * Error interceptor function type
 */
export type ErrorInterceptor = (error: AxiosError) => unknown;

/**
 * A robust API client with pre/post processing capabilities
 */
export class ApiClient {
  private readonly axiosInstance: AxiosInstance;
  private readonly config: ApiClientConfig;

  /**
   * Creates a new ApiClient instance
   * @param config Configuration options for the API client
   */
  constructor(config: ApiClientConfig) {
    this.config = {
      timeout: 10000,
      maxRetries: 3,
      retryDelay: 1000,
      handleErrors: true,
      ...config,
    };

    this.axiosInstance = axios.create({
      baseURL: this.config.baseURL,
      timeout: this.config.timeout,
      headers: this.config.headers,
    });

    // Add default error handling if enabled
    if (this.config.handleErrors) {
      this.addErrorInterceptor(this.defaultErrorHandler);
    }
  }

  /**
   * Add a request interceptor
   * @param interceptor Function to process the request config before sending
   * @returns An ID used to remove the interceptor
   */
  addRequestInterceptor(interceptor: RequestInterceptor): number {
    return this.axiosInstance.interceptors.request.use(interceptor);
  }

  /**
   * Add a response interceptor
   * @param interceptor Function to process the response before returning to caller
   * @returns An ID used to remove the interceptor
   */
  addResponseInterceptor(interceptor: ResponseInterceptor): number {
    return this.axiosInstance.interceptors.response.use(interceptor);
  }

  /**
   * Add an error interceptor
   * @param interceptor Function to process errors before returning to caller
   * @returns An ID used to remove the interceptor
   */
  addErrorInterceptor(interceptor: ErrorInterceptor): number {
    return this.axiosInstance.interceptors.response.use(undefined, interceptor);
  }

  /**
   * Remove a request interceptor
   * @param id The interceptor ID returned when adding
   */
  removeRequestInterceptor(id: number): void {
    this.axiosInstance.interceptors.request.eject(id);
  }

  /**
   * Remove a response interceptor
   * @param id The interceptor ID returned when adding
   */
  removeResponseInterceptor(id: number): void {
    this.axiosInstance.interceptors.response.eject(id);
  }

  /**
   * Make a GET request
   * @param url The URL to request
   * @param config Optional request configuration
   * @returns Promise resolving to the response data
   */
  async get<T = unknown>(url: string, config?: AxiosRequestConfig): Promise<T> {
    return this.request<T>({
      method: 'GET',
      url,
      ...config,
    });
  }

  /**
   * Make a POST request
   * @param url The URL to request
   * @param data The data to send
   * @param config Optional request configuration
   * @returns Promise resolving to the response data
   */
  async post<T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    return this.request<T>({
      method: 'POST',
      url,
      data,
      ...config,
    });
  }

  /**
   * Make a PUT request
   * @param url The URL to request
   * @param data The data to send
   * @param config Optional request configuration
   * @returns Promise resolving to the response data
   */
  async put<T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    return this.request<T>({
      method: 'PUT',
      url,
      data,
      ...config,
    });
  }

  /**
   * Make a PATCH request
   * @param url The URL to request
   * @param data The data to send
   * @param config Optional request configuration
   * @returns Promise resolving to the response data
   */
  async patch<T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    return this.request<T>({
      method: 'PATCH',
      url,
      data,
      ...config,
    });
  }

  /**
   * Make a DELETE request
   * @param url The URL to request
   * @param config Optional request configuration
   * @returns Promise resolving to the response data
   */
  async delete<T = unknown>(url: string, config?: AxiosRequestConfig): Promise<T> {
    return this.request<T>({
      method: 'DELETE',
      url,
      ...config,
    });
  }

  /**
   * Make a request with automatic retry for failed requests
   * @param config The request configuration
   * @returns Promise resolving to the response data
   */
  private async request<T = unknown>(config: AxiosRequestConfig): Promise<T> {
    let retries = 0;
    const maxRetries = this.config.maxRetries ?? 0;
    const retryDelay = this.config.retryDelay ?? 1000;

    while (retries <= maxRetries) {
      try {
        const response = await this.axiosInstance.request<T>(config);
        return response.data;
      } catch (error) {
        if (retries >= maxRetries || !this.shouldRetry(error as AxiosError)) {
          throw error;
        }

        retries++;
        logInfo(`Retrying request (${retries}/${maxRetries})...`);
        await this.delay(retryDelay);
      }
    }

    // This line should never be reached, but TypeScript requires an explicit return
    throw new Error('Maximum retries exceeded');
  }

  /**
   * Determine if a failed request should be retried
   * @param error The error from the failed request
   * @returns True if the request should be retried
   */
  private shouldRetry(error: AxiosError): boolean {
    // Don't retry if there's no response (network error) - these are transient and worth retrying
    if (!error.response) {
      return true;
    }

    const status = error.response.status;
    const data = error.response.data;

    // Don't retry client errors (4xx) - these are validation/bad request errors
    if (status >= 400 && status < 500) {
      return false;
    }

    // For 5xx errors, check if it's a validation error masquerading as a server error
    // Validation errors often have a "message" field with validation details
    if (status >= 500 && status < 600) {
      // If the error has a message field that looks like a validation error, don't retry
      if (
        typeof data === 'object' &&
        data !== null &&
        'message' in data &&
        typeof (data as { message: unknown }).message === 'string'
      ) {
        const message = (data as { message: string }).message;
        // Validation errors typically contain phrases like "must be", "required", "invalid", etc.
        const validationKeywords = [
          'must be',
          'required',
          'invalid',
          'cannot',
          'should',
          'expected',
          'but received',
        ];
        const isValidationError = validationKeywords.some(keyword =>
          message.toLowerCase().includes(keyword.toLowerCase())
        );
        if (isValidationError) {
          return false;
        }
      }
      // Retry other 5xx errors (genuine server errors)
      return true;
    }

    // Don't retry other status codes
    return false;
  }

  /**
   * Default error handler for common error scenarios
   * @param error The error from the failed request
   */
  private readonly defaultErrorHandler = (error: AxiosError): Promise<never> => {
    const errorMessage = getErrorMessage(error);

    // Log the user-friendly error message
    logError(errorMessage);

    if (error.response) {
      logError('Response details:', error.response.status, error.response.data);
    } else if (error.request) {
      logError('No response received from server');
    } else {
      logError('Error setting up request:', error.message);
    }

    return Promise.reject(error);
  };

  /**
   * Helper method to create a delay
   * @param ms Milliseconds to delay
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

/**
 * Create a configured API client instance
 * @param config Configuration options for the API client
 * @returns A new ApiClient instance
 */
export const createApiClient = (config: ApiClientConfig): ApiClient => {
  return new ApiClient(config);
};

/**
 * Example usage:
 *
 * // Create an API client
 * const apiClient = createApiClient({
 *   baseURL: 'https://api.example.com',
 *   headers: {
 *     'Content-Type': 'application/json',
 *   },
 * });
 *
 * // Add authentication token
 * apiClient.addRequestInterceptor(config => {
 *   const token = localStorage.getItem('auth_token');
 *   if (token) {
 *     config.headers = config.headers || {};
 *     config.headers['Authorization'] = `Bearer ${token}`;
 *   }
 *   return config;
 * });
 *
 * // Add response logging
 * apiClient.addResponseInterceptor(response => {
 *   logInfo('Response received:', response.status);
 *   return response;
 * });
 *
 * // Use the client
 * const fetchUsers = async () => {
 *   try {
 *     const users = await apiClient.get('/users');
 *     return users;
 *   } catch (error) {
 *     logError('Failed to fetch users:', error);
 *     throw error;
 *   }
 * };
 */
